import * as SmartySDK from 'smartystreets-javascript-sdk'
import * as sdkUtils from 'smartystreets-javascript-sdk-utils'
import { SMARTY_WEBSITE_KEY } from './constants'

/** Smarty Configuration */
const SmartyCore = SmartySDK.core
const websiteKey = SMARTY_WEBSITE_KEY
const smartySharedCredentials = new SmartyCore.SharedCredentials(websiteKey)
const autoCompleteClientBuilder = new SmartyCore.ClientBuilder(smartySharedCredentials).withLicenses(['us-autocomplete-pro-cloud'])
const autoCompleteClient = autoCompleteClientBuilder.buildUsAutocompleteProClient()
const usStreetClientBuilder = new SmartyCore.ClientBuilder(smartySharedCredentials)

export const generateAddressOption = (address) => {
  const option = { value: 0, label: `${address.address1 || ''} ${address.address2 || ''} ${address.city || ''}, ${address.state || ''} ${address.zipCode || ''}` }

  return option.label !== '  ,  ' ? option : null
}

export const formatAutocompleteSuggestion = (suggestion) => {
  const street = suggestion.streetLine ? `${suggestion.streetLine} ` : ''
  const secondary = suggestion?.secondary ? `${suggestion.secondary} ` : ''
  const entries = suggestion?.entries > 1 ? `(${suggestion.entries}) ` : ''
  const city = suggestion?.city ? `${suggestion.city} ` : ''
  const state = suggestion?.state ? `${suggestion.state}, ` : ''
  const zip = suggestion?.zipcode ? `${suggestion.zipcode}` : ''

  return street + secondary + entries + city + state + zip
}

// Autocomplete query using smarty for suggestions
export const queryAutocompleteForSuggestions = (query: string, setSuggestions: (results: any) => void, callback: (options: any) => void) => {
  const lookup = new SmartySDK.usAutocompletePro.Lookup(query)

  autoCompleteClient.send(lookup)
    .then(results => {
      setSuggestions(results.result)
      callback(results.result.map((suggestion, index) => ({ value: index, label: formatAutocompleteSuggestion(suggestion) })))
    })
    .catch(e => {
      console.warn(e)
      callback([])
    })
}

// Autocomplete query using smarty for entries
export const queryAutocompleteForEntries = (query: string, setSuggestions: (results: any) => void) => {
  const lookup = new SmartySDK.usAutocompletePro.Lookup(query)
  lookup.selected = query

  autoCompleteClient.send(lookup)
    .then(results => {
      setSuggestions(results.result)
    })
    .catch(e => {
      console.warn(e)
    })
}

export const updateStateFromValidatedUsAddress = (response, isAutocomplete = false) => {
  const lookup = response.lookups[0]
  const isValid = sdkUtils.isValid(lookup)
  const isAmbiguous = sdkUtils.isAmbiguous(lookup)
  const isMissingSecondary = sdkUtils.isMissingSecondary(lookup)

  if (!isValid) {
    return { error: 'The address is invalid.' }
  } else if (isAmbiguous) {
    return { error: 'The address is ambiguous.' }
  } else if (isMissingSecondary && !isAutocomplete) {
    return { error: 'The address is missing a secondary number.' }
  } else if (isValid) {
    const candidate = lookup.result[0]
    return { address1: candidate.deliveryLine1, address2: candidate.deliveryLine2 || '', city: candidate.components.cityName, state: candidate.components.state, zipCode: `${candidate.components.zipCode}-${candidate.components.plus4Code}` }
  }
}

export const createLookup = (address1: string, address2: string, city: string, state: string, zipCode: string) => {
  const lookup = new SmartySDK.usStreet.Lookup()
  lookup.street = address1
  lookup.street2 = address2
  lookup.city = city
  lookup.state = state
  lookup.zipCode = zipCode

  return lookup
}

export const validateUSAddress = async (values: any, address1: string, address2: string, city: string, state: string, zipCode: string, type: string ) => {
  // Validate US Address using Smarty
  const usStreetClient = usStreetClientBuilder.buildUsStreetApiClient()
  // Home Address validation
  const lookup = createLookup(values[address1], values[address2], values[city], values[state], values[zipCode])

  if (!!lookup.street) {
    return new Promise((resolve) => {
      usStreetClient.send(lookup)
        .then((response) => { 
          const result = updateStateFromValidatedUsAddress(response, true)
          if ( result.error ) {
            resolve({ error: { text: result.error, type }})
          } else {
            resolve ({
              [address1]: result.address1,
              [address2]: result.address2,
              [city]: result.city,
              [state]: result.state,
              [zipCode]: result.zipCode,
              type
            })
          }
        })
        .catch(e => resolve({ error: { text: e.error, type }}))
    })
  } else {
    return new Promise((resolve) => {
      resolve({ error: { text: 'A street address is required.', type } })
    })
  }
}